import { useEffect, useRef, useState } from "react";
import { Send, Phone, MessageCircle } from "lucide-react";
import { API, CONTACT, whatsappLink } from "@/constants";

const QUICK_REPLIES = [
  "Buy a private jet",
  "Sell my jet",
  "Charter an aircraft",
  "Yacht charter or purchase",
  "Source bullion",
  "Tell me about Vistacoin",
];

const sessionKey = (source) => `avc_concierge_session_${source}`;

const getSession = (source) => {
  let id = localStorage.getItem(sessionKey(source));
  if (!id) {
    id = `${source}-${crypto.randomUUID()}`;
    localStorage.setItem(sessionKey(source), id);
  }
  return id;
};

export const ConciergePanel = ({ source = "concierge", className = "" }) => {
  const [sessionId] = useState(() => getSession(source));
  const [messages, setMessages] = useState([]);
  const [opening, setOpening] = useState("");
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [leadRef, setLeadRef] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/concierge/history/${sessionId}`)
      .then((r) => r.json())
      .then((d) => {
        setMessages(d.messages || []);
        setOpening(d.opening);
        if (d.lead_id) setLeadRef("recorded");
      })
      .catch(() => setOpening("Hello, I'm the Aerovista Concierge. How can we help today?"));
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const content = (text || input).trim();
    if (!content || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: content, source }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const ev = JSON.parse(line.slice(5));
          if (ev.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + ev.content };
              return copy;
            });
          } else if (ev.type === "lead") {
            setLeadRef(ev.reference);
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: `I'm having trouble connecting. Please WhatsApp us on ${CONTACT.whatsappDisplay} or call ${CONTACT.phoneDisplay}.`,
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const showChips = messages.length === 0;

  return (
    <div data-testid="concierge-panel" className={`flex flex-col h-full bg-[#0b0b0b]/95 backdrop-blur-2xl text-white ${className}`}>
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
        <img src="/concierge-avatar.jpg" alt="Aerovista Concierge" className="w-10 h-10 rounded-full ring-1 ring-gold/60 object-cover" />
        <div>
          <p className="font-serif text-lg leading-none">Aerovista Concierge</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold mt-1">Online · replies in seconds</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4 text-sm font-light" data-testid="concierge-messages">
        {opening && <Bubble role="assistant" content={opening} />}
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} typing={streaming && i === messages.length - 1 && !m.content} />
        ))}
        {leadRef && leadRef !== "recorded" && (
          <div data-testid="concierge-lead-confirmation" className="rounded-sm border border-gold/40 bg-gold/10 p-4 text-xs">
            <p className="text-gold uppercase tracking-[0.2em] text-[10px] mb-2">Request recorded · {leadRef}</p>
            <div className="flex gap-3">
              <a href={whatsappLink(`Hello, my Aero Vista Club reference is ${leadRef}.`)} target="_blank" rel="noopener noreferrer" data-testid="concierge-whatsapp-link"
                 className="inline-flex items-center gap-1.5 text-white/80 hover:text-gold transition-colors"><MessageCircle size={13} /> Continue on WhatsApp</a>
              <a href={CONTACT.phoneTel} data-testid="concierge-call-link" className="inline-flex items-center gap-1.5 text-white/80 hover:text-gold transition-colors"><Phone size={13} /> Call now</a>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {showChips && (
        <div className="px-5 pb-3 flex flex-wrap gap-2" data-testid="concierge-quick-replies">
          {QUICK_REPLIES.map((q) => (
            <button key={q} onClick={() => send(q)} data-testid={`concierge-chip-${q.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-[11px] px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors duration-300">
              {q}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 px-4 py-3 border-t border-white/10">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Tell us what you need..." data-testid="concierge-input"
               className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors" />
        <button type="submit" disabled={streaming || !input.trim()} data-testid="concierge-send-button" aria-label="Send"
                className="w-10 h-10 rounded-full bg-gold text-black flex items-center justify-center hover:bg-gold-light disabled:opacity-40 transition-colors">
          <Send size={15} />
        </button>
      </form>
    </div>
  );
};

const Bubble = ({ role, content, typing }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-sm px-4 py-3 leading-relaxed whitespace-pre-wrap ${role === "user" ? "bg-gold text-black" : "bg-white/5 border border-white/10 text-white/85"}`}
         data-testid={`concierge-message-${role}`}>
      {typing ? <span className="inline-flex gap-1 items-center h-4"><Dot /><Dot d="0.15s" /><Dot d="0.3s" /></span> : content}
    </div>
  </div>
);

const Dot = ({ d = "0s" }) => <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: d }} />;
